import React, { useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  Grid,
  CircularProgress,
} from '@mui/material';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { addNotification, setTheme, toggleSidebar, openModal } from '../store';
import { useGetSystemHealthQuery, useGetAgentsQuery } from '../store';

const ReduxDemo: React.FC = () => {
  const dispatch = useAppDispatch();
  
  // Sélection des états Redux
  const { theme, notifications, modals, sidebarCollapsed } = useAppSelector((state) => state.ui);
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const { agents, isLoading: agentsLoading } = useAppSelector((state) => state.agents);

  // Utilisation des hooks RTK Query
  const { data: systemHealth, isLoading: healthLoading } = useGetSystemHealthQuery();
  const { data: agentsFromApi, isLoading: apiAgentsLoading } = useGetAgentsQuery({});

  const handleAddNotification = () => {
    dispatch(addNotification({
      type: 'success',
      title: 'Test Redux',
      message: 'Notification ajoutée avec succès !',
      duration: 5000,
    }));
  };

  const handleToggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    dispatch(setTheme(newTheme));
  };

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
  };

  const handleOpenModal = () => {
    dispatch(openModal('isCreateAgentOpen'));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Redux Toolkit - Démonstration
      </Typography>
      
      <Grid container spacing={3}>
        {/* État d'authentification */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                État d'authentification
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Statut" 
                    secondary={isAuthenticated ? 'Connecté' : 'Non connecté'} 
                  />
                  <Chip 
                    label={isAuthenticated ? 'Authentifié' : 'Non authentifié'} 
                    color={isAuthenticated ? 'success' : 'default'}
                    size="small"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Utilisateur" 
                    secondary={user?.email || 'Aucun utilisateur'} 
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* État de l'UI */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                État de l'interface
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Thème" 
                    secondary={theme} 
                  />
                  <Button size="small" onClick={handleToggleTheme}>
                    Changer
                  </Button>
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Sidebar" 
                    secondary={sidebarCollapsed ? 'Réduite' : 'Étendue'} 
                  />
                  <Button size="small" onClick={handleToggleSidebar}>
                    Toggle
                  </Button>
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Notifications" 
                    secondary={`${notifications.length} active(s)`} 
                  />
                  <Button size="small" onClick={handleAddNotification}>
                    Ajouter
                  </Button>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* État des agents */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                État des agents (Store local)
              </Typography>
              {agentsLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Typography variant="body2">
                  {agents.length} agent(s) dans le store local
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* RTK Query API */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                RTK Query API
              </Typography>
              {healthLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Santé du système : {systemHealth ? 'Connecté' : 'Hors ligne'}
                </Typography>
              )}
              {apiAgentsLoading ? (
                <CircularProgress size={24} sx={{ mt: 1 }} />
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Agents depuis l'API : {agentsFromApi?.length || 0}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Actions et modals */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Actions Redux
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button variant="contained" onClick={handleAddNotification}>
                  Ajouter notification
                </Button>
                <Button variant="outlined" onClick={handleToggleTheme}>
                  Changer thème
                </Button>
                <Button variant="outlined" onClick={handleToggleSidebar}>
                  Toggle Sidebar
                </Button>
                <Button variant="outlined" onClick={handleOpenModal}>
                  Ouvrir Modal
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Notifications actives */}
        {notifications.length > 0 && (
          <Grid item xs={12}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Notifications actives
              </Typography>
              {notifications.map((notification) => (
                <Alert 
                  key={notification.id} 
                  severity={notification.type}
                  sx={{ mb: 1 }}
                >
                  <strong>{notification.title}</strong>: {notification.message}
                </Alert>
              ))}
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ReduxDemo;
